import React from 'react';
import styled from 'styled-components';
import { QuizOption as QuizOptionType } from '../../types/quiz.types';
import { useQuiz } from '../../context/QuizContext';

interface QuizOptionProps {
  option: QuizOptionType;
}

interface StyledOptionProps {
  isSelected: boolean;
  isCorrect?: boolean;
  isAnswered: boolean;
}

const OptionContainer = styled.div<StyledOptionProps>`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  background-color: ${({ theme, isSelected, isCorrect, isAnswered }) => {
    if (!isAnswered) {
      return isSelected 
        ? theme.colors.secondary
        : theme.colors.background.paper;
    }
    
    if (isCorrect) {
      return theme.colors.status.correct;
    }
    
    return isSelected
      ? theme.colors.status.incorrect
      : theme.colors.background.paper;
  }};
  
  border: 1px solid ${({ theme, isSelected, isCorrect, isAnswered }) => {
    if (!isAnswered) {
      return isSelected 
        ? theme.colors.secondary
        : theme.colors.border.light;
    }
    
    if (isCorrect) {
      return theme.colors.status.correct;
    }
    
    return isSelected
      ? theme.colors.status.incorrect
      : theme.colors.border.light;
  }};
  
  transition: all ${({ theme }) => theme.transitions.fast};
  cursor: ${({ isAnswered }) => (isAnswered ? 'default' : 'pointer')};
  
  &:hover {
    background-color: ${({ theme, isAnswered }) => 
      !isAnswered ? `${theme.colors.background.input}` : ''};
  }
`;

const RadioButton = styled.div<StyledOptionProps>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid ${({ theme, isSelected, isCorrect, isAnswered }) => {
    if (!isAnswered) {
      return isSelected 
        ? theme.colors.text.primary
        : theme.colors.border.medium;
    }
    
    if (isCorrect) {
      return theme.colors.status.correct;
    }
    
    return isSelected
      ? theme.colors.status.incorrect
      : theme.colors.border.medium;
  }};
  
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${({ theme }) => theme.spacing.md};
  
  &::after {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: ${({ theme, isSelected, isCorrect, isAnswered }) => {
      if (!isAnswered) {
        return isSelected 
          ? theme.colors.text.primary
          : 'transparent';
      }
      
      if (isCorrect) {
        return theme.colors.status.correct;
      }
      
      return isSelected
        ? theme.colors.status.incorrect
        : 'transparent';
    }};
  }
`;

const OptionContent = styled.div`
  flex: 1;
  display: flex;
`;

const OptionText = styled.span`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 1rem;
`;

const OptionId = styled.div`
  min-width: 24px;
  margin-right: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: bold;
`;

const OptionPercentage = styled.div<{ isAnswered: boolean; isCorrect?: boolean }>`
  margin-left: ${({ theme }) => theme.spacing.md};
  color: ${({ theme, isCorrect, isAnswered }) => {
    if (!isAnswered) return theme.colors.text.secondary;
    return isCorrect ? theme.colors.status.correct : theme.colors.text.secondary;
  }};
  font-weight: ${({ isAnswered }) => (isAnswered ? 'bold' : 'normal')};
  min-width: 50px;
  text-align: right;
`;

const QuizOption: React.FC<QuizOptionProps> = ({ option }) => {
  const { quizState, selectOption } = useQuiz();
  
  const isSelected = quizState.selectedOption === option.id;
  const isAnswered = quizState.isAnswered;
  
  const handleOptionClick = () => {
    if (!isAnswered) {
      selectOption(option.id);
    }
  };
  
  return (
    <OptionContainer
      isSelected={isSelected}
      isCorrect={option.isCorrect}
      isAnswered={isAnswered}
      onClick={handleOptionClick}
    >
      <RadioButton
        isSelected={isSelected}
        isCorrect={option.isCorrect}
        isAnswered={isAnswered}
      />
      
      <OptionContent>
        <OptionId>{option.id}.</OptionId>
        <OptionText>{option.text}</OptionText>
      </OptionContent>
      
      {isAnswered && (
        <OptionPercentage isAnswered={isAnswered} isCorrect={option.isCorrect}>
          [{option.stats.percentage}%]
        </OptionPercentage>
      )}
    </OptionContainer>
  );
};

export default QuizOption; 